import { usePersistentReactiveQueryValue } from '@/hooks/use-persistent-reactive-query-value';
import { useSession } from '@/lib/auth/use-session';
import { useCallback, useEffect, useState } from 'react';

// Query keys (keep for compatibility if needed for manual cache invalidation, though Convex is reactive)
export const orderKeys = {
  all: ['orders'] as const,
  lists: () => [...orderKeys.all, 'list'] as const,
  list: (params?: { limit?: number; offset?: number; status?: string; order_type?: string }) =>
    [...orderKeys.lists(), params] as const,
  order: (orderId: string) => [...orderKeys.all, orderId] as const,
  status: (orderId: string) => [...orderKeys.all, orderId, 'status'] as const,
};

const WEB_ORDER_CACHE_KEY = 'cribnosh_web_order_cache_v1';
const WEB_ORDER_STATUS_CACHE_KEY = 'cribnosh_web_order_status_cache_v1';
const WEB_ORDERS_LIST_CACHE_KEY = 'cribnosh_web_orders_list_cache_v1';

type OrdersApiEnvelope<T> = {
  ok?: boolean;
  result?: T;
  error?: string;
};

const callOrdersApi = async <T>(query: URLSearchParams): Promise<T> => {
  const response = await fetch(`/api/orders?${query.toString()}`, {
    method: 'GET',
    credentials: 'same-origin',
    cache: 'no-store',
  });

  const payload = (await response.json().catch(() => null)) as OrdersApiEnvelope<T> | null;
  if (!response.ok || !payload?.ok) {
    throw new Error(payload?.error || 'Orders request failed.');
  }

  return payload.result as T;
};

export interface OrderItemType {
  dish_id: string;
  name: string;
  price: number;
  quantity: number;
  nativePrice?: number;
  nativeCurrency?: string;
  displayPrice?: number;
  displayCurrency?: string;
  displayTotalPrice?: number;
  [key: string]: unknown;
}

export interface OrderType {
  _id?: string;
  id: string;
  order_id: string;
  currency?: string;
  displayCurrency?: string;
  total_amount: number;
  totalAmount: number;
  displayTotalAmount?: number;
  displaySubtotal?: number;
  displayDeliveryFee?: number;
  displayTax?: number;
  order_status: string;
  orderStatus: string;
  status?: string;
  payment_status: string;
  paymentStatus: string;
  payment_method?: string | null;
  paymentMethod?: string | null;
  payment_link_token?: string | null;
  paymentLinkToken?: string | null;
  order_items: OrderItemType[];
  orderItems: OrderItemType[];
  order_date?: string | number;
  orderDate?: string | number;
  createdAt?: string | number;
  _creationTime?: string | number;
  special_instructions?: string;
  specialInstructions?: string;
  estimated_prep_time_minutes?: number;
  estimatedPrepTimeMinutes?: number;
  chef_notes?: string;
  chefNotes?: string;
  foodCreatorNotes?: string;
  [key: string]: unknown;
}

type OrderResponseRecord = Omit<Partial<OrderType>, 'order_items' | 'orderItems'> & {
  order_items?: OrderItemType[];
};

/**
 * Hook to get order details
 */
export function useOrder(orderId: string | null) {
  const { user, isLoading: authLoading } = useSession();
  const [orderLive, setOrderLive] = useState<OrderResponseRecord | undefined>(undefined);
  const [orderError, setOrderError] = useState<Error | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const canQueryOrder = Boolean(orderId && user?._id && !authLoading);

  const fetchOrder = useCallback(async () => {
    if (!canQueryOrder || !orderId) {
      setOrderLive(undefined);
      setOrderError(null);
      return;
    }

    setIsFetching(true);
    try {
      const query = new URLSearchParams({ orderId });
      const result = await callOrdersApi<OrderResponseRecord>(query);
      setOrderLive(result);
      setOrderError(null);
    } catch (error) {
      setOrderLive(undefined);
      setOrderError(
        error instanceof Error ? error : new Error('Failed to load order.'),
      );
    } finally {
      setIsFetching(false);
    }
  }, [canQueryOrder, orderId]);

  useEffect(() => {
    void fetchOrder();
  }, [fetchOrder]);

  const { value: order, hasCachedValue } = usePersistentReactiveQueryValue<OrderResponseRecord>({
    storageKey: WEB_ORDER_CACHE_KEY,
    scopeKey: `${orderId || 'none'}:cookie-session`,
    liveValue: orderLive,
    enabled: Boolean(orderId),
  });

  const mappedOrder = order ? {
    ...order,
    id: order.order_id,
    totalAmount: order.total_amount,
    orderStatus: order.order_status,
    paymentStatus: order.payment_status,
    orderItems: order.order_items || [],
    specialInstructions: order.special_instructions,
    estimatedPrepTimeMinutes: order.estimated_prep_time_minutes,
    chefNotes: order.chef_notes,
  } : undefined;

  return {
    data: mappedOrder as OrderType | undefined,
    isLoading: orderId !== null && (authLoading || (canQueryOrder && isFetching && orderLive === undefined && !hasCachedValue)),
    error: orderError,
  };
}

/**
 * Hook to get order status
 */
export function useOrderStatus(orderId: string | null) {
  const { user, isLoading: authLoading } = useSession();
  const [orderLive, setOrderLive] = useState<OrderResponseRecord | undefined>(undefined);
  const [orderError, setOrderError] = useState<Error | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const canQueryOrder = Boolean(orderId && user?._id && !authLoading);

  const fetchOrderStatus = useCallback(async () => {
    if (!canQueryOrder || !orderId) {
      setOrderLive(undefined);
      setOrderError(null);
      return;
    }

    setIsFetching(true);
    try {
      const query = new URLSearchParams({ orderId });
      const result = await callOrdersApi<OrderResponseRecord>(query);
      setOrderLive(result);
      setOrderError(null);
    } catch (error) {
      setOrderLive(undefined);
      setOrderError(
        error instanceof Error ? error : new Error('Failed to load order status.'),
      );
    } finally {
      setIsFetching(false);
    }
  }, [canQueryOrder, orderId]);

  useEffect(() => {
    void fetchOrderStatus();
  }, [fetchOrderStatus]);

  const { value: order, hasCachedValue } = usePersistentReactiveQueryValue<OrderResponseRecord>({
    storageKey: WEB_ORDER_STATUS_CACHE_KEY,
    scopeKey: `${orderId || 'none'}:cookie-session`,
    liveValue: orderLive,
    enabled: Boolean(orderId),
  });

  return {
    data: order,
    isLoading: orderId !== null && (authLoading || (canQueryOrder && isFetching && orderLive === undefined && !hasCachedValue)),
    error: orderError,
  };
}

/**
 * Hook to get orders list
 */
export function useOrdersList(params?: {
  limit?: number;
  offset?: number;
  status?: 'ongoing' | 'past' | 'all';
  order_type?: 'individual' | 'group' | 'all';
}): { data: { orders: OrderType[] } | undefined; isLoading: boolean; error: Error | null } {
  const { user, isLoading: authLoading } = useSession();
  const [loaderTimedOut, setLoaderTimedOut] = useState(false);
  const [ordersLive, setOrdersLive] = useState<OrderType[] | undefined>(undefined);
  const [ordersError, setOrdersError] = useState<Error | null>(null);
  const [isFetching, setIsFetching] = useState(false);

  const canQueryOrders = !authLoading && !!user?._id;

  const fetchOrders = useCallback(async () => {
    if (!canQueryOrders) {
      setOrdersLive(undefined);
      setOrdersError(null);
      return;
    }

    setIsFetching(true);
    try {
      const query = new URLSearchParams({ list: '1' });
      if (params?.status) query.set('status', params.status);
      if (params?.order_type) query.set('order_type', params.order_type);
      if (typeof params?.limit === 'number') query.set('limit', String(params.limit));
      if (typeof params?.offset === 'number') query.set('offset', String(params.offset));

      const result = await callOrdersApi<OrderType[]>(query);
      setOrdersLive(result);
      setOrdersError(null);
    } catch (error) {
      setOrdersLive(undefined);
      setOrdersError(
        error instanceof Error ? error : new Error('Failed to load orders.'),
      );
    } finally {
      setIsFetching(false);
    }
  }, [canQueryOrders, params?.limit, params?.offset, params?.order_type, params?.status]);

  useEffect(() => {
    void fetchOrders();
  }, [fetchOrders]);

  const ordersScopeKey = [
    user?._id || 'guest',
    'cookie-session',
    params?.status || 'all',
    params?.order_type || 'all',
    params?.limit ?? 'default',
    params?.offset ?? 0,
  ].join(':');
  const { value: orders, hasCachedValue } = usePersistentReactiveQueryValue<OrderType[]>({
    storageKey: WEB_ORDERS_LIST_CACHE_KEY,
    scopeKey: ordersScopeKey,
    liveValue: ordersLive as OrderType[] | undefined,
    enabled: canQueryOrders,
  });

  useEffect(() => {
    if (!canQueryOrders || ordersLive !== undefined || hasCachedValue || !isFetching) {
      setLoaderTimedOut(false);
      return;
    }

    const timeout = window.setTimeout(() => {
      setLoaderTimedOut(true);
    }, 4000);

    return () => window.clearTimeout(timeout);
  }, [canQueryOrders, hasCachedValue, isFetching, ordersLive]);

  const isLoading = authLoading || (canQueryOrders && isFetching && ordersLive === undefined && !hasCachedValue && !loaderTimedOut);
  const error = loaderTimedOut
    ? new Error('Orders query timed out')
    : ordersError;

  return {
    data: orders ? { orders: orders as OrderType[] } : undefined,
    isLoading,
    error,
  };
}
